import { type AssetFileKind } from './fileTypes';

/**
 * Current protocol version for plugin communication
 * Increment when making breaking changes to the URL param contract
 */
export const PLUGIN_PROTOCOL_VERSION = 1;

/**
 * Minimum protocol version supported by the app
 * Update this when dropping support for older plugin versions
 */
export const MIN_SUPPORTED_PROTOCOL_VERSION = 1;

/**
 * URL parameter names for plugin configuration
 */
export const PluginConfigParam = {
  AssetTypes: 'assetTypes',
  FileExtensions: 'fileExtensions',
  HostOrigin: 'hostOrigin',
  OpenSelectedAssetModal: 'openSelectedAssetModal',
  ProjectId: 'projectId',
  ProtocolVersion: 'protocolVersion',
  SelectedAsset: 'selectedAsset',
  WorkspaceId: 'workspaceId',
} as const;

/**
 * Selected asset data passed via URL param as JSON
 * Contains info about the currently selected asset for highlighting
 */
export type SelectedAssetParam = {
  /** Asset ID (groups versions together) */
  assetId: string;
  /** Version/Clip ID (specific version of the asset) */
  versionId: string;
  /** Workspace ID where this asset belongs */
  workspaceId: string;
  /** Version reference mode used when the asset was inserted */
  versionReference?: 'static' | 'dynamic';
  /** Previously selected width */
  width?: number;
  /** Previously selected height */
  height?: number;
};

/**
 * Plugin configuration passed via URL params
 */
export type PluginConfig = {
  /**
   * Protocol version for breaking change detection
   */
  protocolVersion: number;
  /**
   * Origin of the host embedding the plugin iframe (e.g., the Sanity Studio URL)
   */
  hostOrigin?: string;
  /**
   * Sanity project ID (for analytics tracking)
   */
  projectId?: string;
  /**
   * ID of the workspace to browse
   */
  workspaceId?: string;
  /**
   * Filter assets by type (e.g., ['image', 'video', 'pdf'])
   * If not provided, all asset types are shown.
   */
  assetTypes?: AssetFileKind[];
  /**
   * Filter assets by file extension (e.g., ['jpg', 'png', 'pdf'])
   * If not provided, all file extensions are shown.
   */
  fileExtensions?: string[];
  /**
   * Currently selected asset info (for highlighting in the picker)
   */
  selectedAsset?: SelectedAssetParam;
  /** Auto-open the asset detail modal when selectedAsset is provided. Defaults to false. */
  openSelectedAssetModal?: boolean;
};
